/**
 * Identification Page - ID selection and entry step
 *
 * Screen for selecting ID type and entering ID information
 * Part of the sequential flow: Calculator → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, IdCard } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { FormReminder } from "@/components/forms/FormReminder";
import { RampsButton } from "@/components/ramps/Button";
import { getSubdivisions } from "@/constants/subdivisions";
import { isBankService } from "@/domain/calculator";
import { useContinue } from "@/hooks/useContinue";
import { useCreateProfile } from "@/hooks/useCreateProfile";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { toast } from "@/lib/toast.tsx";
import { RampsInput, RampsSelect } from "../components/ramps/Input";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "../components/ui/select";
import { useCountries } from "../hooks/useCountries";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Function to normalize idType from old format to new format
const normalizeIdType = (idType: string): string => {
	const legacyMapping: Record<string, string> = {
		"state-id": "STA",
		"driver-license": "DRV",
		passport: "PAS",
		"government-id": "GOV",
		"alien-id": "ALN",
	};
	return legacyMapping[idType] || idType;
};

// Function to determine default ID type based on address and payout countries
const getDefaultIdType = (
	addressCountry: string,
	payoutCountry: string,
): string => {
	// If both countries are not set, return default
	if (
		!addressCountry ||
		!payoutCountry ||
		addressCountry.trim() === "" ||
		payoutCountry.trim() === ""
	) {
		return "GOV";
	}

	// If both addressCountry and payoutCountry are USA or CAN, default to DRV
	const northAmericanCountries = ["USA", "CAN"];
	if (
		northAmericanCountries.includes(addressCountry) &&
		northAmericanCountries.includes(payoutCountry)
	) {
		return "DRV";
	}

	// If addressCountry and payoutCountry are different, default to PAS
	if (addressCountry !== payoutCountry) {
		return "PAS";
	}

	// Otherwise, default to GOV
	return "GOV";
};

// Function to get ID types based on destination country
const getIdTypesForCountry = (
	destinationCountry: string,
	countryOptions: Array<{ value: string; label: string }>,
) => {
	const country = countryOptions.find((c) => c.value === destinationCountry);
	const countryName = country?.label || destinationCountry;

	if (destinationCountry === "USA") {
		// USA: All ID types including Alien ID and State ID
		return [
			{ value: "GOV", label: t`Government ID` },
			{ value: "DRV", label: t`Driver's License` },
			{ value: "PAS", label: t`Passport` },
			{ value: "ALN", label: t`Alien ID` },
			{ value: "STA", label: t`State ID` },
		];
	} else if (destinationCountry === "CAN") {
		// Canada: All ID types except Alien ID
		return [
			{ value: "GOV", label: t`Government ID` },
			{ value: "DRV", label: t`Driver's License` },
			{ value: "PAS", label: t`Passport` },
			{ value: "STA", label: t`State ID` },
		];
	} else if (destinationCountry === "ARG") {
		return [
			{ value: "PAS", label: t`Passport` },
			{ value: "GOV", label: t`${countryName} Government ID` },
			{ value: "ALN", label: t`Alien ID/Precaria` },
		];
	} else {
		// Other countries: Only Passport and Government ID (prefixed with country name)
		return [
			{ value: "PAS", label: t`Passport` },
			{ value: "GOV", label: t`${countryName} Government ID` },
		];
	}
};

function IdentificationPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("identification", { page_name: "identification" });
	}, [trackPageView]);

	// Generate unique ID for form field
	const idNumberFieldId = useId();

	// Use countries hook for centralized data access
	const { countryOptions } = useCountries();

	// Get destination country from store (payout country)
	const destinationCountry = store.payoutCountry || "USA";

	// Get dynamic ID types based on destination country
	const ID_TYPES = getIdTypesForCountry(destinationCountry, countryOptions);

	// State variables
	const [idType, setIdType] = useState(normalizeIdType(store.idType));
	const [idNumber, setIdNumber] = useState(store.idNumber);
	const [issueCountry, setIssueCountry] = useState(store.idIssueCountry);
	const [issueState, setIssueState] = useState(store.idIssueState);
	// Track if user has manually selected idType (or if store already has a meaningful value)
	const [hasUserSelectedIdType, setHasUserSelectedIdType] = useState(
		!!store.idType && store.idType !== "state-id", // Don't consider default "state-id" as user selection
	);
	// Track if user has manually selected issueCountry on THIS page (not auto-populated)
	const [hasUserSelectedIssueCountry, setHasUserSelectedIssueCountry] =
		useState(false);

	// Token and transaction_id are synced globally by URLParamsInitializer
	// Initialize auth token with fallback logic (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;

	// Profile creation hook
	const {
		createProfileIfNeeded,
		isCreating,
		isAssociating,
		createError,
		associateError,
	} = useCreateProfile(authToken);

	// Handle profile creation errors
	useEffect(() => {
		if (createError) {
			toast.error(
				createError.message || t`Failed to create profile. Please try again.`,
			);
		}
	}, [createError]);

	// Handle wallet association errors
	useEffect(() => {
		if (associateError) {
			toast.error(
				associateError.message ||
					t`Failed to associate wallet. Please try again.`,
			);
		}
	}, [associateError]);

	const selectedCountry = countryOptions.find((c) => c.value === issueCountry);
	const selectedState = getSubdivisions(issueCountry).find(
		(s) => s.value === issueState,
	);

	// Constants for better maintainability
	const COUNTRIES_WITH_STATES = ["USA", "CAN", "MEX"];
	const STATE_ISSUED_DOCUMENTS = ["DRV", "STA"]; // Driver's License, State ID

	// Function to determine if state selection should be shown
	const shouldShowIssueState = useCallback(
		(docType: string, country: string): boolean => {
			// Only show state for countries that have states
			if (!COUNTRIES_WITH_STATES.includes(country)) {
				return false;
			}

			// Only show state for document types that are issued at state level
			return STATE_ISSUED_DOCUMENTS.includes(docType);
		},
		[],
	);

	// Set initial issue country from payout country
	useEffect(() => {
		// Only set default if user hasn't manually selected an issue country
		if (hasUserSelectedIssueCountry) {
			return;
		}

		const hasCountries = countryOptions.length > 0;
		const hasPayoutCountry = !!store.payoutCountry;
		const addressCountry = store.addressCountry || "";

		if (hasCountries && hasPayoutCountry) {
			// Use payout country as initial issue country (most common case)
			const initialIssueCountry =
				store.idIssueCountry || addressCountry || destinationCountry;
			const countryExists = countryOptions.find(
				(c) => c.value === initialIssueCountry,
			);

			if (countryExists && issueCountry !== initialIssueCountry) {
				setIssueCountry(initialIssueCountry);
			}
		}
	}, [
		countryOptions,
		destinationCountry,
		store.payoutCountry,
		store.addressCountry,
		store.idIssueCountry,
		hasUserSelectedIssueCountry,
		issueCountry,
	]);

	// Clear issue state when switching to a country or document type that doesn't require states
	useEffect(() => {
		if (!shouldShowIssueState(idType, issueCountry)) {
			setIssueState("");
		}
	}, [idType, issueCountry, shouldShowIssueState]);

	// Set default idType based on address and payout countries
	useEffect(() => {
		// Only set default if user hasn't manually selected an idType
		if (hasUserSelectedIdType) {
			return;
		}

		// Wait for countries to be available and ID_TYPES to be loaded
		if (
			!store.addressCountry ||
			!store.payoutCountry ||
			ID_TYPES.length === 0
		) {
			return;
		}

		// Calculate the default idType
		const defaultIdType = getDefaultIdType(
			store.addressCountry,
			store.payoutCountry,
		);

		// Check if the default idType is available in the current ID_TYPES options
		const isDefaultAvailable = ID_TYPES.some(
			(type) => type.value === defaultIdType,
		);

		// If the calculated default is not available, try fallback options
		let finalIdType = defaultIdType;
		if (!isDefaultAvailable) {
			// Fallback priority: GOV -> PAS -> first available option
			const fallbackOptions = ["GOV", "PAS"];
			finalIdType =
				fallbackOptions.find((option) =>
					ID_TYPES.some((type) => type.value === option),
				) ||
				ID_TYPES[0]?.value ||
				"GOV";
		}

		// Only update if the current idType is different
		if (finalIdType && idType !== finalIdType) {
			setIdType(finalIdType);
		}
	}, [
		store.addressCountry,
		store.payoutCountry,
		hasUserSelectedIdType,
		ID_TYPES,
		idType,
	]);

	const handleSubmit = async () => {
		// Validate required fields - issue state is only required for certain countries
		// Use store as source of truth for issue country
		const currentIssueCountry = store.idIssueCountry || issueCountry;
		let digitalProfileID = store.digitalProfileId || "";
		const isValidForm =
			idType &&
			idNumber &&
			currentIssueCountry &&
			(shouldShowIssueState(idType, currentIssueCountry) ? issueState : true);

		if (!isValidForm) {
			toast.validationError("Please fill in all required fields");
			return;
		}

		// Check if we have required transaction data (supports both v2 and v5 API)
		if (!store.transactionId || !authToken) {
			toast.error("Missing transaction information. Please start over.");
			return;
		}

		// Save identification data to store first
		rampsActions.setIdentificationData({
			idType,
			idNumber,
			idIssueCountry: currentIssueCountry,
			idIssueState: shouldShowIssueState(idType, currentIssueCountry)
				? issueState
				: "",
		});

		// Try to create profile if all data is available (backup safety net)
		// TanStack Store updates are synchronous, read fresh state directly
		if (isBankService(store.serviceOption) && !store.digitalProfileId) {
			digitalProfileID =
				(await createProfileIfNeeded({ requireAllFields: true })) || "";
		}

		// Navigate to the next appropriate page
		if (
			(isBankService(store.serviceOption) && digitalProfileID) ||
			!isBankService(store.serviceOption)
		) {
			handleContinue(search);
		}
	};

	const formatIdNumber = (value: string) => {
		// Allow alphanumeric characters, hyphens, asterisks, and spaces
		// Remove any characters that don't match the backend regex pattern
		return value.replace(/[^a-zA-Z0-9\-*\s]/g, "").toUpperCase();
	};

	const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatIdNumber(e.target.value);
		setIdNumber(formatted);
	};

	const handleCountryChange = (value: string) => {
		setIssueCountry(value);
		setIssueState("");
		setHasUserSelectedIssueCountry(true);
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={80}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Identification`}
					</RampsText>
				</div>

				{/* Form */}
				<div className="space-y-6 flex-1">
					<div className="space-y-4">
						{/* ID Type */}
						<RampsSelect label={t`ID type`}>
							<Select
								value={idType}
								onValueChange={(value) => {
									setIdType(value);
									setHasUserSelectedIdType(true);
								}}
							>
								<SelectTrigger className="w-full border-none bg-transparent p-0 h-auto shadow-none">
									<RampsText
										variant="body-default"
										color="primary"
										className="font-medium"
									>
										{ID_TYPES.find((type) => type.value === idType)?.label ||
											t`Select ID type`}
									</RampsText>
								</SelectTrigger>
								<SelectContent className="bg-ramps-background-base border-ramps-border-default">
									{ID_TYPES.map((type) => (
										<SelectItem key={type.value} value={type.value}>
											<RampsText variant="body-default" color="primary">
												{type.label}
											</RampsText>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</RampsSelect>

						{/* ID Number */}
						<RampsInput
							id={idNumberFieldId}
							type="text"
							label={t`ID number`}
							value={idNumber}
							onChange={handleIdNumberChange}
						/>

						{/* ID Issue Country */}
						<RampsSelect label={t`ID issue country`}>
							<Select value={issueCountry} onValueChange={handleCountryChange}>
								<SelectTrigger className="w-full border-none bg-transparent p-0 h-auto shadow-none">
									<div className="flex items-center gap-3">
										<CircleFlag
											countryCode={selectedCountry?.countryCode || "us"}
											className="w-6 h-6"
										/>
										<RampsText
											variant="body-default"
											color="primary"
											className="font-medium"
										>
											{selectedCountry?.label}
										</RampsText>
									</div>
								</SelectTrigger>
								<SelectContent className="bg-ramps-background-base border-ramps-border-default">
									{countryOptions.map((country) => (
										<SelectItem key={country.value} value={country.value}>
											<div className="flex items-center gap-3">
												<CircleFlag
													countryCode={country.countryCode}
													className="w-6 h-6"
												/>
												<RampsText variant="body-default" color="primary">
													{country.label}
												</RampsText>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</RampsSelect>

						{/* Issue State - Only show for state-issued documents in countries that have states */}
						{shouldShowIssueState(idType, issueCountry) && (
							<RampsSelect label={t`Issue state`}>
								<Select value={issueState} onValueChange={setIssueState}>
									<SelectTrigger className="w-full border-none bg-transparent p-0 h-auto shadow-none">
										<RampsText
											variant="body-default"
											color="primary"
											className="font-medium"
										>
											{selectedState?.label || t`Select state`}
										</RampsText>
									</SelectTrigger>
									<SelectContent className="bg-ramps-background-base border-ramps-border-default">
										{getSubdivisions(issueCountry).map((state) => (
											<SelectItem key={state.value} value={state.value}>
												<RampsText variant="body-default" color="primary">
													{state.label}
												</RampsText>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</RampsSelect>
						)}

						{/* Information notice */}
						<div className="flex gap-3">
							<FormReminder
								text={t`You'll need to show this ID at the MoneyGram location`}
								icon={IdCard}
							/>
						</div>
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={handleSubmit}
						disabled={
							!idType ||
							!idNumber ||
							!(store.idIssueCountry || issueCountry) ||
							(shouldShowIssueState(
								idType,
								store.idIssueCountry || issueCountry,
							) &&
								!issueState) ||
							isCreating ||
							isAssociating
						}
						loading={isCreating || isAssociating}
						className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/identification")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: IdentificationPage,
});
